import { useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Link, MessagePlugin, Popconfirm, Space, Table } from 'tdesign-react';
import { isVoid, renderDateTime } from '@/utils/common';
import { deleteLib, getLibs } from '@/services/execComponentService';
import OperateTitle from '@/pages/Maintenance/Executive/components/OperateTitle';
import useTable from '@/hooks/useTable';
import { setComponentCurrentLib } from '@/slices/execComponentSlice';
import MannualUpdateVersion from '@/pages/Maintenance/Resource/Env/components/MannualUpdateVersion';
import AutoUpdateVersion from '@/pages/Maintenance/Resource/Env/components/AutoUpdateVersion';

function ChoiceLib({ clusterId, envId, setComponentCurrentLib, currentLib, productLibId }) {
  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');

  const columns = [
    {
      colKey: 'row-select',
      type: 'single',
      fixed: 'left',
      width: 46,
    },
    {
      colKey: 'version',
      title: '版本信息',
      width: 100,
    },
    {
      title: '版本信息描述',
      colKey: 'productDesc',
      width: 120,
      cell: ({ row }) => row.productDesc || '-',
    },
    {
      title: '远端路径',
      colKey: 'path',
    },
    {
      title: '部署机器数',
      colKey: 'deployNum',
      width: 120,
    },
    {
      title: '创建时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Popconfirm theme='danger' content='确定删除？' onConfirm={() => doDelete(row)}>
          <Link theme='danger' hover='color'>
            删除
          </Link>
        </Popconfirm>
      ),
      width: 120,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    setComponentCurrentLib(null);
    const { data } = await getLibs({ ...params, componentStencilId: id, envId, clusterId });
    if (!isEmpty(data)) {
      setComponentCurrentLib(data[0]);
    }

    setData(data);
  });

  const { config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
  });

  useEffect(() => {
    if (!isVoid(clusterId)) {
      doSearch();
    }
  }, [clusterId, doSearch]);

  const doDelete = async (row) => {
    await deleteLib(row.id);
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  const onSelectChange = (keys, { selectedRowData }) => {
    setComponentCurrentLib(selectedRowData[0]);
  };

  return (
    !isVoid(clusterId) && (
      <Root>
        <OperateTitle
          label='第二步：'
          title='选择需要的物料'
          operator={
            <Space size='small'>
              <MannualUpdateVersion
                onFinish={doRefresh}
                componentStencilId={id}
                productLibId={productLibId}
                envId={envId}
                clusterId={clusterId}
              />
              <AutoUpdateVersion
                productLibId={productLibId}
                componentStencilId={id}
                onFinish={doRefresh}
              />
            </Space>
          }
        />
        <Table
          {...config}
          resizable={false}
          rowKey='id'
          maxHeight={250}
          selectedRowKeys={[currentLib?.id]}
          onSelectChange={onSelectChange}
        />
      </Root>
    )
  );
}

export default connect(
  (state) => ({
    envId: state.execComponent.currentEnv.id,
    productLibId: state.execComponent.currentEnv.productLibId,
    clusterId: state.execComponent.currentCluster.id,
    currentLib: state.execComponent.currentLib,
  }),
  {
    setComponentCurrentLib,
  },
)(ChoiceLib);

const Root = styled.div`
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-bottom: 16px;
  overflow: hidden;
  .t-table {
    padding: 16px 20px;
  }
  .t-table__header--fixed:not(.t-table__header--multiple) > tr > th {
    background-color: transparent;
  }
`;
